/**
 * The scroll position survives a LAYOUT SWAP (#897 follow-up).
 *
 * Crossing the phone/desktop breakpoint rebuilds every screen (see
 * `draft-store.ts`), and a rebuilt scroll container starts at the top. This
 * hook remembers WHERE the user was — the item at the top of the view, not a
 * pixel offset — and puts the new screen's container back there. Pixels don't
 * carry over: the two layouts have different widths, so text wraps and rows
 * grow or shrink.
 *
 * - The position is recorded when scrolling pauses (by the time a swap is
 *   detected the viewport has already resized, and the old screen may have
 *   reflowed), and read live at swap start only if there is no fresh record.
 *   A swap during a restore still under way carries that restore on.
 * - It names the item at the top edge (or the nearest one above it, with the
 *   pixel gap past its end). Fallback: the fraction of the way down — only
 *   where the list is fully there in both layouts (not a paged file list).
 * - The new screen restores it once that item exists (content often loads
 *   after mount), and keeps at it while the page is too short to get there
 *   (the content below still loading). If it never appears, the fraction is
 *   used once content has rendered and stopped changing. The user scrolling
 *   first wins, and nothing happens after the claim window.
 * - Same lifetime as drafts: only a swap keeps it, and a screen still mounted
 *   when the swap window closes (the chat home's 950px swap leaves Settings
 *   alone) drops what it stashed. A normal page change still opens at the top.
 *
 * Items: `[data-scroll-anchor]` / `[data-file-id]` (the value is the key),
 * else headings, table rows and list items, keyed by their text (+ which
 * occurrence). A sticky header inside the container (`[data-scroll-sticky]`)
 * is not "the view": the item is put back just below it. Other chrome inside
 * the container (a sidebar nav, a section index) is marked `[data-scroll-skip]`.
 */
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react'
import { claimSwapStash, onLayoutSwapEnd, onLayoutSwapStart, peekSwapStash, stashForSwap, DRAFT_CLAIM_WINDOW_MS } from './draft-store'

/** What a swap carries. */
export interface SwapScrollPos {
  /** The item at (or nearest above) the top of the view; null: none. */
  anchor: string | null
  /** Where the view's top edge is relative to it: a share of its height when
   *  inside it (0..1); `afterPx` past its end when the view is in the gap
   *  after it; `beforePx` above it when the view is above the first item. */
  within: number
  afterPx?: number
  beforePx?: number
  /** How far down the container was, 0..1. */
  fraction: number
  /** When the swap that carried it started (ms): nothing happens after the
   *  claim window from THEN, however late the new screen mounts. */
  at?: number
}

export interface SwapScrollOptions {
  /** Which elements count as items. */
  items?: string
  /** Fall back to the same fraction of the page when the item can't be found.
   *  Off for paged lists (Drive's "Load more"): the other layout starts with
   *  fewer rows, so a fraction lands on an unrelated one. Default: on. */
  fraction?: boolean
}

const DEFAULT_ITEMS = '[data-scroll-anchor], h1, h2, h3, h4, tr, li'

const textKey = (el: Element) => (el.textContent ?? '').replace(/\s+/g, ' ').trim().slice(0, 80)

/** Every candidate item in `root`, with a key stable across the two layouts. */
function anchorsIn(root: HTMLElement, selector: string): Array<{ el: HTMLElement; key: string }> {
  const seen = new Map<string, number>()
  const out: Array<{ el: HTMLElement; key: string }> = []
  const nested = new Map<Element, boolean>()
  for (const el of Array.from(root.querySelectorAll<HTMLElement>(selector))) {
    // Chrome, not content: a sticky header, a sidebar nav, a section index,
    // an inline dialog (`fixed` — it doesn't move when the page scrolls).
    if (el.closest('[data-scroll-sticky], [data-scroll-skip], .fixed')) continue
    if (el.getClientRects().length === 0) continue // not rendered (display:none)
    // Inside a box with its own scroll (a long table capped at a height): its
    // place depends on THAT scroll, which the other layout starts at the top.
    if (inNestedScroller(el, root, nested)) continue
    // An id both layouts render for the same item beats its text (a file row
    // shows different columns on the phone).
    const explicit = el.getAttribute('data-scroll-anchor') ?? el.getAttribute('data-file-id')
    const base = explicit ? `a:${explicit}` : `${el.tagName}:${textKey(el)}`
    if (!explicit && base.length <= el.tagName.length + 1) continue // no text to key it by
    const n = seen.get(base) ?? 0
    seen.set(base, n + 1)
    out.push({ el, key: `${base}#${n}` })
  }
  return out
}

/** Whether a box between `el` and `root` scrolls on its own (cached per box). */
function inNestedScroller(el: HTMLElement, root: HTMLElement, cache: Map<Element, boolean>): boolean {
  for (let p = el.parentElement; p && p !== root; p = p.parentElement) {
    let own = cache.get(p)
    if (own === undefined) {
      const oy = getComputedStyle(p).overflowY
      own = (oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight + 1
      cache.set(p, own)
    }
    if (own) return true
  }
  return false
}

/** The top edge of the VISIBLE part of the container: below a sticky header,
 *  counted whether or not it has stuck yet (at the top of the page it can
 *  still sit a few px down; once scrolled it covers the content). */
function viewTop(root: HTMLElement): number {
  let top = root.getBoundingClientRect().top
  for (const s of Array.from(root.querySelectorAll<HTMLElement>('[data-scroll-sticky]'))) {
    const h = s.getBoundingClientRect().height
    if (h > 0) top += h
  }
  return top
}

/** Where `root` is scrolled to, as something the other layout can find. */
export function readSwapScroll(root: HTMLElement, selector = DEFAULT_ITEMS): SwapScrollPos {
  const max = root.scrollHeight - root.clientHeight
  const fraction = max > 0 ? root.scrollTop / max : 0
  if (root.scrollTop <= 0) return { anchor: null, within: 0, fraction: 0 }
  const top = viewTop(root)
  let above: { key: string; r: DOMRect } | null = null
  for (const { el, key } of anchorsIn(root, selector)) {
    const r = el.getBoundingClientRect()
    if (r.height <= 0) continue
    if (r.top > top) {
      // The view's top is above this item. With an item above it, keep the
      // gap past THAT one's end (form content between headings); else (above
      // the first item) the gap to this one. Pixels, not a share of the
      // item's height: a gap scaled by a short heading's height went wild.
      if (above) return { anchor: above.key, within: 1, afterPx: top - above.r.bottom, fraction }
      return { anchor: key, within: 0, beforePx: r.top - top, fraction }
    }
    if (r.bottom > top) return { anchor: key, within: (top - r.top) / r.height, fraction } // straddles it
    above = { key, r }
  }
  if (above) return { anchor: above.key, within: 1, afterPx: top - above.r.bottom, fraction }
  return { anchor: null, within: 0, fraction }
}

/** Put `root` back at `pos`. False while the item isn't there, or the page is
 *  too short to bring it to the top yet. `final`: settle for what's possible. */
export function applySwapScroll(
  root: HTMLElement, pos: SwapScrollPos, selector = DEFAULT_ITEMS, final = false, fraction = true,
): boolean {
  const anchors = anchorsIn(root, selector)
  if (pos.anchor) {
    const hit = anchors.find(a => a.key === pos.anchor)
    if (hit) {
      const target = () => {
        const r = hit.el.getBoundingClientRect()
        const at = pos.afterPx !== undefined ? r.bottom + pos.afterPx
          : pos.beforePx !== undefined ? r.top - pos.beforePx
          : r.top + pos.within * r.height
        return at - viewTop(root)
      }
      const before = target()
      const was = root.scrollTop
      root.scrollTop += before
      const after = target()
      // An item that didn't move with the scroll is not in this container's
      // flow (a nested scroller, an overlay): stop rather than keep pushing.
      if (Math.abs(root.scrollTop - was) >= 1 && Math.abs(after - before) < 1) return true
      // Reached — or capped because the content below is still loading (the
      // container isn't tall enough yet): then not done, try again as it grows.
      return Math.abs(after) < 2 || final
    }
  }
  // No item to find (or it never appeared): the fraction, once the content
  // has rendered and stopped changing — never on a "Loading…" page, never on
  // a paged list.
  if (!fraction || !final) return false
  const max = root.scrollHeight - root.clientHeight
  if (max <= 0 || (pos.anchor && anchors.length === 0)) return false
  root.scrollTop = pos.fraction * max
  return true
}

/**
 * Keep `ref`'s scroll position across a layout swap. `key` names the place
 * (e.g. `settings:budget`, `drive:<folder>`); null turns it off.
 */
export function useSwapScroll(key: string | null, ref: RefObject<HTMLElement | null>, opts: SwapScrollOptions = {}): void {
  const stashKey = key ? `scroll:${key}` : null
  const items = opts.items ?? DEFAULT_ITEMS
  const fraction = opts.fraction ?? true
  /** Recorded once scrolling pauses, before any resize reflows the screen. */
  const lastPos = useRef<SwapScrollPos | null>(null)
  /** A restore still under way on this screen: what a second swap carries on
   *  (its container hasn't got there yet — reading it would say "top"). */
  const restoring = useRef<SwapScrollPos | null>(null)

  useEffect(() => {
    const root = ref.current
    if (!stashKey || !root) return
    lastPos.current = null
    let pause: ReturnType<typeof setTimeout> | undefined
    const record = () => { pause = undefined; if (root.clientHeight > 0) lastPos.current = readSwapScroll(root, items) }
    // When scrolling pauses, not per frame: on a long table a read is a scan of
    // every row's text.
    const onScroll = () => { clearTimeout(pause); pause = setTimeout(record, RECORD_PAUSE_MS) }
    root.addEventListener('scroll', onScroll, { passive: true })
    let stashed = false
    const offStart = onLayoutSwapStart(() => {
      if (root.clientHeight === 0) return // hidden (a keep-alive deck): nothing on screen to keep
      const pos = restoring.current
        ?? (pause !== undefined || root.scrollTop <= 0 || !lastPos.current ? readSwapScroll(root, items) : lastPos.current)
      stashForSwap(stashKey, { ...pos, at: restoring.current?.at ?? Date.now() })
      stashed = true
    })
    // Still mounted when the window closes: this screen was not rebuilt (the
    // chat home's 950px swap leaves Settings alone) — drop what IT stashed, or
    // a later visit to this page within the claim window jumps to it. Only
    // that: a screen the swap just mounted must not drop the position it is
    // still restoring.
    const offEnd = onLayoutSwapEnd(() => {
      if (stashed) claimSwapStash(stashKey)
      stashed = false
    })
    return () => {
      root.removeEventListener('scroll', onScroll)
      clearTimeout(pause)
      offStart()
      offEnd()
    }
  }, [stashKey, ref, items])

  // Put it back on the screen the swap mounted, once the content is there.
  useLayoutEffect(() => {
    if (!stashKey) return
    const root = ref.current
    const pos = peekSwapStash<SwapScrollPos>(stashKey)
    if (!root || !pos) return
    if (pos.anchor === null && pos.fraction === 0) { claimSwapStash(stashKey); return }
    const left = DRAFT_CLAIM_WINDOW_MS - (Date.now() - (pos.at ?? Date.now()))
    if (left <= 0) { claimSwapStash(stashKey); return } // mounted too late: it's over
    restoring.current = pos
    let done = false
    let quiet: ReturnType<typeof setTimeout> | undefined
    let frame = 0
    /** Stop watching. Not a claim: StrictMode's dev cleanup → re-run must
     *  find the position again. */
    const detach = () => {
      done = true
      restoring.current = null
      observer.disconnect()
      clearTimeout(quiet)
      clearTimeout(giveUp)
      if (frame) cancelAnimationFrame(frame)
      for (const ev of USER_SCROLL) root.removeEventListener(ev, stop)
      window.removeEventListener('keydown', onKey)
    }
    /** Restored, or given up for good: the position is used. */
    const finish = () => {
      if (done) return
      detach()
      claimSwapStash(stashKey)
    }
    const attempt = (final = false) => {
      if (done) return
      if (applySwapScroll(root, pos, items, final, fraction)) { finish(); return }
      // Nothing rendered yet ("Loading…" for longer than the settle time):
      // keep waiting for the content — the next change tries again.
      if (final && anchorsIn(root, items).length > 0) { finish(); return }
      if (final) return
      // The item isn't there (yet). Once the content stops changing for a
      // moment, settle for what's possible — this layout may not render that
      // item at all. Never later than that: a jump long after the user
      // started reading would be worse than the top.
      clearTimeout(quiet)
      quiet = setTimeout(() => attempt(true), SETTLE_MS)
    }
    // The user scrolled first: their position wins — the keyboard too, wherever
    // focus is (just after a swap it is on the body, not in this container).
    const stop = () => finish()
    const onKey = (e: KeyboardEvent) => {
      // A space or arrow typed into a field is typing, not scrolling.
      const t = e.target as HTMLElement | null
      if (t?.closest?.('input, textarea, select, [contenteditable=""], [contenteditable="true"]')) return
      if (SCROLL_KEYS.has(e.key)) finish()
    }
    // One attempt per frame, however many DOM changes it brings.
    const observer = new MutationObserver(() => {
      if (frame || done) return
      frame = requestAnimationFrame(() => { frame = 0; attempt() })
    })
    observer.observe(root, { childList: true, subtree: true })
    for (const ev of USER_SCROLL) root.addEventListener(ev, stop, { passive: true })
    window.addEventListener('keydown', onKey)
    const giveUp = setTimeout(finish, left)
    attempt()
    return detach
  }, [stashKey, ref, items, fraction])
}

const USER_SCROLL = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const
const SCROLL_KEYS = new Set(['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', ' '])
/** Content quiet this long with no match: settle for what's possible. */
const SETTLE_MS = 400
/** Scrolling paused this long: record where the view is. */
const RECORD_PAUSE_MS = 150
